// @flow
import * as React from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import shortid from 'shortid'

import { Button, Flex } from 'components/common'
import { InputController, SelectController } from 'components/common/form'
import { useI18n } from '~/core/i18n'

import type { SelectOption } from 'components/common/form'
import type { Mapping } from '../types.flow'

type InputTypeProps = {
  id: string,
  type: 'input' | 'textarea',
  name: string,
  rules?: {
    [key: string]: any
  }
}

type SelectTypeProps = {
  id: string,
  type: 'select',
  name: string,
  options: SelectOption[],
  rules?: {
    [key: string]: any
  }
}

type CustomTypeProps = {
  id: string,
  type: 'custom',
  Component: ({ defaultValues: any }) => React.Node
}

export type DynamicFieldProps = InputTypeProps | SelectTypeProps | CustomTypeProps

export type EditMappingProps = {
  mapping?: Mapping,
  customFields?: DynamicFieldProps[],
  customTexts?: {
    key?: string,
    value?: string
  },
  onSubmit?: (data: Mapping) => void
}

const DynamicComponent = ({ defaultValues, ...props }: DynamicFieldProps & { defaultValues: any }) => {
  // Will be populated in case of need
  switch (props.type) {
    case 'input':
      return <InputController {...props} />
    case 'textarea':
      return <InputController {...props} type="textarea" />
    case 'select':
      return <SelectController {...props} />
    case 'custom':
      return <props.Component defaultValues={defaultValues} />
    default:
      return null
  }
}

const EditMappingModal = ({ mapping = {}, customFields, customTexts, onSubmit = () => {} }: EditMappingProps) => {
  const [msg] = useI18n()
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      ...mapping
    }
  })

  const handleSubmit = (data: $Diff<Mapping, { id: string }>) => {
    onSubmit({
      id: mapping.id || shortid.generate(),
      ...data
    })
  }

  return (
    <FormProvider {...methods}>
      <form
        style={{
          width: 450
        }}
        onSubmit={methods.handleSubmit(handleSubmit)}
      >
        <Flex column>
          {customFields ? (
            customFields.map(field => <DynamicComponent key={field.id} defaultValues={mapping} {...(field: any)} />)
          ) : (
            <>
              <InputController
                id="header-name-input"
                label={customTexts?.key || msg('common.controllers.mappings.key')}
                name="name"
                rules={{ required: msg('common.rules.requiredField') }}
              />
              <InputController
                id="header-value-textarea"
                label={customTexts?.value || msg('common.controllers.mappings.value')}
                name="value"
                rows="4"
                rules={{ required: msg('common.rules.requiredField') }}
                type="textarea"
              />
            </>
          )}
        </Flex>
        <Flex justifyCenter style={{ marginTop: '1.5rem' }}>
          <Button type="submit">{msg('common.button.save')}</Button>
        </Flex>
      </form>
    </FormProvider>
  )
}

export { EditMappingModal }
